import React, { FunctionComponent, useContext, useEffect, useState } from "react";
import {
    ButtonProps,
    CommandBarButton,
    createErrorFailure,
    DefaultButton,
    FormValidationSummary,
    LoadingButton,
    Modal,
    Stack,
    Text,
    validationFailed,
    ValidationResult
} from "sprint-common-web";

import { MergeEpisodesRequest } from "../../../../api/MergeEpisodesRequest";
import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { ValidateMergeEpisodesRequest } from "../../../../api/ValidateMergeEpisodesRequest";
import { SeasonEpisodeListingContext } from "../context/SeasonEpisodeListingContext";
import { EpisodeListingItem } from "../EpisodeListingItem";

export const MergeEpisodesCommandBarButton: FunctionComponent<ButtonProps> = (props: ButtonProps) => {
    const {
        episodesToMerge,
        selectedTvSeries,
        selectedSeason,
        clearEpisodesToMerge,
        updateListAfterMerge
    } = useContext(SeasonEpisodeListingContext);

    const { mergeEpisodes, validateMergeEpisodes } = useAssetsManageTvSeriesApi();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidationLoading, setIsValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        setValidationResult(undefined);

        if (!isModalOpen) return;

        setIsValidationLoading(true);

        validateMergeEpisodes(selectedTvSeries.id, createValidateRequest(episodesToMerge))
            .then((res) => setValidationResult(res))
            .catch((err) => setValidationResult(validationFailed([createErrorFailure(JSON.stringify(err))])))
            .finally(() => setIsValidationLoading(false));
    }, [isModalOpen]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const isMergeButtonDisabled = () => {
        return episodesToMerge.length !== 2;
    };

    const handleMergeButtonClick = async (): Promise<void> => {
        setIsLoading(true);
        const response = await mergeEpisodes(
            selectedTvSeries.id,
            createMergeRequest(episodesToMerge, selectedSeason.id)
        );
        await updateListAfterMerge(response.episodeId);
        closeModal();
        clearEpisodesToMerge();
        setIsLoading(false);
    };

    const isMergeConfirmDisabled = isLoading || isValidationLoading || !validationResult?.isValid;

    return (
        <>
            <CommandBarButton
                onClick={openModal}
                text="Merge"
                icon="Merge"
                disabled={isMergeButtonDisabled() || props.disabled}
            />
            <Modal isBlocking={true} isOpen={isModalOpen} headerText="Merge Episodes" onDismiss={closeModal}>
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text>You are about to Merge:</Text>
                    {episodesToMerge.map((episode: EpisodeListingItem) => {
                        return <Text key={episode.id}>{episode.title}</Text>;
                    })}
                </Stack>
                <Stack tokens={{ padding: "10px 0" }}>
                    <FormValidationSummary validationResult={validationResult} isValidating={isValidationLoading} />
                </Stack>
                <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="end" horizontalAlign="end">
                    <DefaultButton text="Cancel" onClick={closeModal} disabled={isLoading} />
                    <LoadingButton
                        text="Merge"
                        isLoading={isLoading}
                        onClick={handleMergeButtonClick}
                        disabled={isMergeConfirmDisabled}
                    />
                </Stack>
            </Modal>
        </>
    );
};

const createValidateRequest = (episodeItems: EpisodeListingItem[]): ValidateMergeEpisodesRequest => {
    return {
        firstEpisodeId: episodeItems[0].id,
        secondEpisodeId: episodeItems[1].id
    };
};

const createMergeRequest = (episodeItems: EpisodeListingItem[], seasonId: string): MergeEpisodesRequest => {
    return {
        firstEpisodeId: episodeItems[0].id,
        secondEpisodeId: episodeItems[1].id,
        seasonId
    };
};
